import { useCoreStore } from '@/stores/coreStore'
import { useEyeDexStore } from '@/stores/eyeDexStore'
import { useEyeStore } from '@/stores/eyeStore'
import { toK } from '@/utils'
import { OHLCV_TF, TICKS_LIMIT } from '@/utils/consts'
import { TickUtils, type Tick } from '@/utils/ticks'
import { computed, ref, type Ref } from 'vue'
import { useRoute } from 'vue-router'
import { useAutoRefresh } from '../useAutorefresh'
import { useEyeTickPusher } from '../useEyeTickPusher'

interface ObPrice {
  price: number | null
  vol: number | null
}

export const useCexDexTicksHandler = (
  cexPrice: Ref<number | null>,
  dexPrice: Ref<number | null>,
  cexPriceBuy: Ref<ObPrice | null>,
  cexPriceSell: Ref<ObPrice | null>,
  dexPriceBuy: Ref<number | null>,
  dexPriceSell: Ref<number | null>,
) => {
  const cexTicks = ref<Tick[]>([])
  const dexTicks = ref<Tick[]>([])
  const cexAsks = ref<Tick[]>([])
  const cexBids = ref<Tick[]>([])
  const dexAsks = ref<Tick[]>([])
  const dexBids = ref<Tick[]>([])

  const route = useRoute()

  const eyeStore = useEyeStore()
  const eyeDexStore = useEyeDexStore()
  const coreStore = useCoreStore()

  const fetchCexChartData = async (exchange: string, symbol: string) => {
    const asset = symbol.split('/')[0]
    const actionId = `${exchange}:${asset}`.toLocaleLowerCase()
    const ticks = await eyeStore.fetchSymbolTicks(actionId, exchange, symbol)
    const tickerActionId = `ticker/${exchange}/${asset}`
    if (ticks) {
      const tillTs = +new Date() / 1000
      cexTicks.value = TickUtils.fillTicksTo(ticks, tillTs).slice(-ticksLimit.value)
      const ohlcv = TickUtils.ticksToOhlcv(cexTicks.value, OHLCV_TF)

      let vol24h = null
      try {
        const r = await coreStore.fetchTicker(tickerActionId, exchange, symbol)
        vol24h = toK(Number(r?.vol24h ?? 0))
      } catch {}

      return {
        ohlcv,
        vol24h,
        error: false,
      }
    }
    return null
  }

  const fetchDexChartData = async (chain: string, contract: string) => {
    const gmgnTicksActionId = `gmgn-ticks/${contract}/${chain}`

    const ticks = await eyeDexStore.fetchGmgnTicks(gmgnTicksActionId, contract, chain)
    if (ticks) {
      const tillTs = +new Date() / 1000
      dexTicks.value = TickUtils.fillTicksTo(ticks, tillTs).slice(-ticksLimit.value)
      const ohlcv = TickUtils.ticksToOhlcv(dexTicks.value, OHLCV_TF)
      return {
        ohlcv,
        error: false,
      }
    }
    return null
  }

  const ticksLimit = computed(() => {
    if (route.query.ticksLimit) {
      return Math.round(Number(route.query.tickLimit))
    }
    return TICKS_LIMIT
  })

  const cexTickPusher = useEyeTickPusher(cexPrice, cexTicks, { limit: ticksLimit.value })
  const dexTickPusher = useEyeTickPusher(dexPrice, dexTicks, { limit: ticksLimit.value })
  const cexAsksPusher = useEyeTickPusher(
    computed(() => cexPriceBuy.value?.price ?? null),
    cexAsks,
    { limit: ticksLimit.value },
  )
  const cexBidsPusher = useEyeTickPusher(
    computed(() => cexPriceSell.value?.price ?? null),
    cexBids,
    { limit: ticksLimit.value },
  )
  const dexAsksPusher = useEyeTickPusher(
    computed(() => dexPriceBuy.value ?? null),
    dexAsks,
    { limit: ticksLimit.value },
  )
  const dexBidsPusher = useEyeTickPusher(
    computed(() => dexPriceSell.value ?? null),
    dexBids,
    { limit: ticksLimit.value },
  )

  const updateTicks = () => {
    cexTickPusher.push()
    dexTickPusher.push()
    cexAsksPusher.push()
    cexBidsPusher.push()
    dexAsksPusher.push()
    dexBidsPusher.push()
  }

  const ticksRefresher = useAutoRefresh(updateTicks, 500)

  return {
    cexTicks,
    dexTicks,
    cexAsks,
    cexBids,
    dexAsks,
    dexBids,
    ticksLimit,
    ticksRefresher,
    fetchCexChartData,
    fetchDexChartData,
  }
}
